/**
 * UI Context
 *
 * Manages UI state for agent sessions:
 * - Tab order for multiple agent sessions
 * - Split view configuration
 * - Focused session tracking
 *
 * Integrates with WorkspaceSessionContext for persistent storage.
 * Requires an active WorkspaceSession - no localStorage fallback.
 *
 * @module contexts/UIContext
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useWorkspaceSession } from './WorkspaceSessionContext.jsx';
import { useAddAgentToSession, useRemoveAgentFromSession, useAddAgentToSplit, useRemoveAgentFromSplit } from '../hooks/useWorkspaceSessions.js';

// DEPRECATED localStorage keys - cleaned up on app start
const DEPRECATED_STORAGE_KEYS = [
  'claude-webui-v2-sessions',
  'claude-webui-v2-sessions-migrated',
  'claude-webui-v2-last-active-session',
];

const UIContext = createContext(null);

// Initial state structure - consolidated to prevent re-render cascade
const createInitialSessionState = () => ({
  tabOrder: [],
  splitSessions: [],
  focusedAgentId: null,
  scrollPositions: {},
});

export function UIProvider({ children }) {
  // Cleanup deprecated localStorage keys on mount (one-time)
  useEffect(() => {
    DEPRECATED_STORAGE_KEYS.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        console.log(`[UIContext] Cleaned up deprecated localStorage key: ${key}`);
      }
    });
  }, []);

  // Get workspace session context (may be null if not available)
  let workspaceSession = null;
  try {
    workspaceSession = useWorkspaceSession();
  } catch {
    // WorkspaceSessionProvider not available (e.g., in popout)
  }

  // Mutations for persisting agent changes to backend
  const addAgentMutation = useAddAgentToSession();
  const removeAgentMutation = useRemoveAgentFromSession();
  const addToSplitMutation = useAddAgentToSplit();
  const removeFromSplitMutation = useRemoveAgentFromSplit();

  // Consolidated session state - single state object to prevent multiple re-renders
  const [sessionState, setSessionState] = useState(createInitialSessionState);

  // Scroll positions ref for synchronous access - state updates are async
  const scrollPositionsRef = useRef({});

  // Track if we've loaded initial state
  const initialLoadRef = useRef(false);
  const lastActiveSessionIdRef = useRef(null);
  // Ref for save timeout - declared here so load effect can cancel it
  const saveTimeoutRef = useRef(null);
  // Flag to prevent save during load (prevents stale closure data being saved)
  const isLoadingSessionRef = useRef(false);
  // Flag to track if we've ever loaded data from a workspace session (not just initialized)
  const hasLoadedFromWorkspaceRef = useRef(false);

  // Load UI state from active workspace session or localStorage
  // Use activeSession ID as dependency - object reference changes on every render
  const activeSessionId = workspaceSession?.activeSession?.id;
  const activeSessionData = workspaceSession?.activeSession;

  useEffect(() => {
    // If workspace session is available and has an active session, load from it
    if (activeSessionId && activeSessionData) {
      // Only reload if session changed
      if (lastActiveSessionIdRef.current !== activeSessionId) {
        // CRITICAL: Set loading flag FIRST to prevent save effect from running
        // This must happen before any state updates
        isLoadingSessionRef.current = true;

        // Cancel any pending save timeout
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }

        // Update ref before state updates
        lastActiveSessionIdRef.current = activeSessionId;

        // Load new session data - SINGLE state update instead of 4
        const positions = activeSessionData.scrollPositions || {};
        scrollPositionsRef.current = positions;

        setSessionState({
          tabOrder: activeSessionData.agentIds || [],
          splitSessions: activeSessionData.splitAgentIds || [],
          focusedAgentId: activeSessionData.focusedAgentId || null,
          scrollPositions: positions,
        });

        initialLoadRef.current = true;
        hasLoadedFromWorkspaceRef.current = true;

        // Clear loading flag after React has processed the state update
        // Use setTimeout to ensure we're past the current render cycle
        setTimeout(() => {
          isLoadingSessionRef.current = false;
        }, 0);
      }
      return;
    }

    // No workspace session - just mark as initialized
    // (localStorage fallback removed - sessions now require WorkspaceSession)
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId]);

  // Save UI state - to workspace session (via auto-save) or localStorage
  useEffect(() => {
    // Skip if not initialized yet
    if (!initialLoadRef.current) return;

    // CRITICAL: Skip if we're currently loading a session
    // This prevents stale closure data from the previous session being saved
    if (isLoadingSessionRef.current) return;

    // CRITICAL: Don't save to workspace session until we've loaded from it at least once
    // This prevents overwriting server data with empty/stale client state on first load
    if (workspaceSession?.activeSession && !hasLoadedFromWorkspaceRef.current) return;

    // Only save if the session ID matches what we loaded
    const currentSessionId = workspaceSession?.activeSession?.id;
    if (currentSessionId && currentSessionId !== lastActiveSessionIdRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      // Double-check: still not loading and session ID hasn't changed
      if (isLoadingSessionRef.current) return;
      const sessionIdNow = workspaceSession?.activeSession?.id;
      if (sessionIdNow && sessionIdNow !== lastActiveSessionIdRef.current) return;

      // Save to workspace session via API (localStorage fallback removed)
      if (workspaceSession?.activeSession && workspaceSession.queueAutoSave) {
        // Ensure focusedAgentId is null instead of empty string (backend expects UUID or null)
        const sanitizedFocusedAgentId = sessionState.focusedAgentId || null;

        // Ensure scrollPositions values are numbers, not strings
        const sanitizedScrollPositions = {};
        for (const [key, value] of Object.entries(scrollPositionsRef.current)) {
          sanitizedScrollPositions[key] = typeof value === 'number' ? value : Number(value) || 0;
        }

        // Note: agentIds/splitAgentIds are managed via dedicated endpoints
        // (POST/DELETE /workspace-sessions/:id/agents/:agentId)
        // Only save UI state fields that are accepted by PUT
        workspaceSession.queueAutoSave({
          focusedAgentId: sanitizedFocusedAgentId,
          scrollPositions: sanitizedScrollPositions,
        });
      }
      // No workspace session = no save (sessions require WorkspaceSession)
    }, 500); // Shorter debounce since WorkspaceSession has its own 2s debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // IMPORTANT: Use activeSession?.id instead of workspaceSession object
    // The workspaceSession object reference changes on every session switch,
    // which would trigger a save. We only want to save when actual UI state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionState, workspaceSession?.activeSession?.id]);

  // Reorder tabs
  const reorderTabs = useCallback((fromIndex, toIndex) => {
    setSessionState(prev => {
      const result = [...prev.tabOrder];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return { ...prev, tabOrder: result };
    });
  }, []);

  // Add agent to tab order
  const addTab = useCallback((agentId) => {
    setSessionState(prev => {
      if (prev.tabOrder.includes(agentId)) {
        // Already in tabs, just focus it
        return { ...prev, focusedAgentId: agentId };
      }
      // Persist to backend immediately (fire-and-forget)
      const sessionId = workspaceSession?.activeSession?.id;
      if (sessionId) {
        addAgentMutation.mutate({ sessionId, agentId });
      }
      return {
        ...prev,
        tabOrder: [...prev.tabOrder, agentId],
        focusedAgentId: agentId,
      };
    });
  }, [workspaceSession?.activeSession?.id, addAgentMutation]);

  // Remove agent from tab order
  const removeTab = useCallback((agentId) => {
    // Persist to backend immediately (fire-and-forget)
    const sessionId = workspaceSession?.activeSession?.id;
    if (sessionId) {
      removeAgentMutation.mutate({ sessionId, agentId });
    }
    setSessionState(prev => {
      const filtered = prev.tabOrder.filter(id => id !== agentId);
      const newFocusedAgentId = prev.focusedAgentId === agentId
        ? (filtered[0] || null)
        : prev.focusedAgentId;
      return {
        ...prev,
        tabOrder: filtered,
        splitSessions: prev.splitSessions.filter(id => id !== agentId),
        focusedAgentId: newFocusedAgentId,
      };
    });
  }, [workspaceSession?.activeSession?.id, removeAgentMutation]);

  // Add agent to split view
  const addToSplit = useCallback((agentId) => {
    setSessionState(prev => {
      if (prev.splitSessions.includes(agentId)) return prev;
      if (prev.splitSessions.length >= 4) return prev; // Max 4 panels
      // Persist to backend immediately (fire-and-forget)
      const sessionId = workspaceSession?.activeSession?.id;
      if (sessionId) {
        addToSplitMutation.mutate({ sessionId, agentId });
      }
      return { ...prev, splitSessions: [...prev.splitSessions, agentId] };
    });
  }, [workspaceSession?.activeSession?.id, addToSplitMutation]);

  // Remove agent from split view
  const removeFromSplit = useCallback((agentId) => {
    // Persist to backend immediately (fire-and-forget)
    const sessionId = workspaceSession?.activeSession?.id;
    if (sessionId) {
      removeFromSplitMutation.mutate({ sessionId, agentId });
    }
    setSessionState(prev => ({
      ...prev,
      splitSessions: prev.splitSessions.filter(id => id !== agentId),
    }));
  }, [workspaceSession?.activeSession?.id, removeFromSplitMutation]);

  // Clear split view
  const clearSplit = useCallback(() => {
    // Persist each removal to backend (fire-and-forget)
    const sessionId = workspaceSession?.activeSession?.id;
    if (sessionId) {
      setSessionState(prev => {
        prev.splitSessions.forEach(agentId => {
          removeFromSplitMutation.mutate({ sessionId, agentId });
        });
        return { ...prev, splitSessions: [] };
      });
    } else {
      setSessionState(prev => ({ ...prev, splitSessions: [] }));
    }
  }, [workspaceSession?.activeSession?.id, removeFromSplitMutation]);

  // Toggle split view for agent
  const toggleSplit = useCallback((agentId) => {
    const sessionId = workspaceSession?.activeSession?.id;
    setSessionState(prev => {
      if (prev.splitSessions.includes(agentId)) {
        // Remove from split - persist to backend
        if (sessionId) {
          removeFromSplitMutation.mutate({ sessionId, agentId });
        }
        return { ...prev, splitSessions: prev.splitSessions.filter(id => id !== agentId) };
      }
      if (prev.splitSessions.length >= 4) return prev;
      // Add to split - persist to backend
      if (sessionId) {
        addToSplitMutation.mutate({ sessionId, agentId });
      }
      return { ...prev, splitSessions: [...prev.splitSessions, agentId] };
    });
  }, [workspaceSession?.activeSession?.id, addToSplitMutation, removeFromSplitMutation]);

  // Set focused agent ID
  const setFocusedAgentId = useCallback((agentId) => {
    setSessionState(prev => ({ ...prev, focusedAgentId: agentId }));
  }, []);

  // Save scroll position for an agent - uses ref for synchronous access
  const saveScrollPosition = useCallback((agentId, scrollTop) => {
    // Update ref immediately (synchronous)
    scrollPositionsRef.current = { ...scrollPositionsRef.current, [agentId]: scrollTop };
    // Also update state to trigger save
    setSessionState(prev => {
      if (prev.scrollPositions[agentId] === scrollTop) return prev;
      return { ...prev, scrollPositions: { ...prev.scrollPositions, [agentId]: scrollTop } };
    });
  }, []);

  // Get scroll position for an agent - reads from ref for immediate access
  const getScrollPosition = useCallback((agentId) => {
    return scrollPositionsRef.current[agentId] || 0;
  }, []);

  // Destructure for backward compatibility - consumers get individual properties
  const { tabOrder, splitSessions, focusedAgentId } = sessionState;

  const value = useMemo(() => ({
    tabOrder,
    splitSessions,
    focusedAgentId,
    focusedSessionId: focusedAgentId, // Alias for compatibility
    setFocusedAgentId,
    setFocusedSessionId: setFocusedAgentId, // Alias for compatibility
    reorderTabs,
    addTab,
    removeTab,
    addToSplit,
    removeFromSplit,
    clearSplit,
    toggleSplit,
    saveScrollPosition,
    getScrollPosition,
  }), [
    tabOrder,
    splitSessions,
    focusedAgentId,
    setFocusedAgentId,
    reorderTabs,
    addTab,
    removeTab,
    addToSplit,
    removeFromSplit,
    clearSplit,
    toggleSplit,
    saveScrollPosition,
    getScrollPosition,
  ]);

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
}
